import React, { useCallback, useMemo } from 'react';
import { Button, Overlay, Text, Stack, LoadingBalls } from '@moda/om';
import { useParams, useHistory } from 'react-router-dom';
import { reportError } from '../../../lib/reportError';
import { getNetworkErrors, isGenericError } from '../../../lib/error';
import { hrefFor } from '../../../routers';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { ReturnMethodValue, useReturnSummary } from '../../../hooks/useReturnSummary';
import { useRequestReturnMutation } from '../../../hooks/useRequestReturnMutation';
import { tc } from '../../../lib/trackingContext';
import { useIsGuestUser } from '../../../hooks/useIsGuestUser';

import './RequestReturnActions.scss';

export const RequestReturnActions: React.FC = () => {
  const history = useHistory();
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { showErrorModal } = useErrorModal();
  const { returnSummary } = useReturnSummary({ orderNumber });
  const { isGuestUser } = useIsGuestUser();
  const [requestReturn, { loading }] = useRequestReturnMutation(orderNumber);

  const handleSubmit = useCallback(async () => {
    try {
      const response = await requestReturn();
      if (response.data) {
        history.push(
          hrefFor.ReturnConfirmationPage({
            shipmentNumber: response.data.requestReturn.data[1].attributes.shipmentNumber
          })
        );
      }

      if (isGuestUser) {
        tc.track('Guest Product Return Requested', {
          addToScopeOnce: {
            returnSummary
          }
        });
      }

      if (!isGuestUser) {
        tc.track('Product Return Requested', {
          addToScopeOnce: {
            returnSummary
          }
        });
      }
    } catch (error) {
      const errorMessage =
        isGenericError(error) && getNetworkErrors(error)?.length
          ? getNetworkErrors(error)?.[0].title
          : 'We could not process your return, please contact Customer Care.';
      reportError('REQUEST_RETURN_SUBMIT', error);
      showErrorModal(errorMessage);
    }
  }, [requestReturn, isGuestUser, history, returnSummary, showErrorModal]);

  const canSubmit = useMemo(() => {
    if (!returnSummary) return false;

    const { selectedItemIds, returnReasonHash } = returnSummary.attributes;

    if (selectedItemIds.length === 0) {
      return false;
    }

    if (returnSummary.attributes.returnMethodId === null) return false;

    if (!selectedItemIds.every(id => returnReasonHash[id])) {
      return false;
    }

    if (isGuestUser && returnSummary.attributes.returnMethodId === ReturnMethodValue.SiteCredit) {
      return false;
    }

    return true;
  }, [isGuestUser, returnSummary]);

  if (!returnSummary) return null;

  return (
    <div className="RequestReturnActions">
      <Stack space={2} direction="horizontal">
        <Button secondary to={hrefFor.OrderDetailPage({ orderNumber })}>
          Cancel
        </Button>

        <Button disabled={!canSubmit} onClick={handleSubmit}>
          Request Return
        </Button>
      </Stack>

      <Overlay show={loading}>
        <LoadingBalls />

        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </div>
  );
};
